import React from 'react';
import { Form, Switch, InputNumber, Radio, Input, Card, Row, Col, Collapse } from 'antd';
import type { RequestSettingsProps } from './types';
import { useTranslation } from 'react-i18next';

const { TextArea } = Input;

const RequestSettings: React.FC<RequestSettingsProps> = ({ platforms }) => {
  const { t } = useTranslation();
  
  // Fix the parser type for InputNumber
  const parsePercentageValue = (value: string | undefined): 0 | 100 => {
    if (!value) return 0;
    const parsed = parseFloat(value.replace('%', ''));
    if (isNaN(parsed) || parsed <= 0) return 0;
    return parsed >= 100 ? 100 : 0;
  };

  const renderPlatformSettings = (platform: { id: string; label: string }) => (
    <Card 
      title={platform.label}
      variant="outlined"
      style={{ marginBottom: 16 }}
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Form.Item
            name={['platformConfigs', platform.id, 'useCustomScore']}
            label={t('settings.requestSettings.platformSettings.customScore')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate
          >
            {({ getFieldValue }) => {
              const useCustom = getFieldValue(['platformConfigs', platform.id, 'useCustomScore']);
              return useCustom ? (
                <Form.Item
                  name={['platformConfigs', platform.id, 'validationScore']}
                  label={t('settings.requestSettings.platformSettings.validationScore')}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    formatter={value => `${value}%`}
                    parser={parsePercentageValue}
                  />
                </Form.Item>
              ) : null;
            }}
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            name={['platformConfigs', platform.id, 'useCustomBonus']}
            label={t('settings.requestSettings.platformSettings.customBonus')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate
          >
            {({ getFieldValue }) => {
              const useCustom = getFieldValue(['platformConfigs', platform.id, 'useCustomBonus']);
              return useCustom ? (
                <>
                  <Form.Item
                    name={['platformConfigs', platform.id, 'bonusType']}
                    label={t('settings.requestSettings.platformSettings.bonusType')}
                  >
                    <Radio.Group>
                      <Radio value="fixed">{t('settings.requestSettings.fixedAmount')}</Radio>
                      <Radio value="percentage">{t('settings.requestSettings.percentage')}</Radio>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item
                    name={['platformConfigs', platform.id, 'bonus']}
                    label={t('settings.requestSettings.platformSettings.bonusAmount')}
                  >
                    <InputNumber min={0} />
                  </Form.Item>
                  <Form.Item
                    name={['platformConfigs', platform.id, 'bonusLoads']}
                    label={t('settings.requestSettings.loadsNumber')}
                  >
                    <InputNumber min={0} precision={0} />
                  </Form.Item>
                </>
              ) : null;
            }}
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name={['platformConfigs', platform.id, 'useCustomApprovedMessage']}
            label={t('settings.requestSettings.platformSettings.customAppMessage')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate
          >
            {({ getFieldValue }) => {
              const useCustom = getFieldValue(['platformConfigs', platform.id, 'useCustomApprovedMessage']);
              return useCustom ? (
                <Form.Item
                  name={['platformConfigs', platform.id, 'approvedMessage']}
                  label={t('settings.requestSettings.platformSettings.approvedMessage')}
                >
                  <TextArea rows={4} />
                </Form.Item>
              ) : null;
            }}
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item
            name={['platformConfigs', platform.id, 'useCustomRejectedMessage']}
            label={t('settings.requestSettings.platformSettings.customRejMessage')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate
          >
            {({ getFieldValue }) => {
              const useCustom = getFieldValue(['platformConfigs', platform.id, 'useCustomRejectedMessage']);
              return useCustom ? (
                <Form.Item
                  name={['platformConfigs', platform.id, 'rejectedMessage']}
                  label={t('settings.requestSettings.platformSettings.rejectedMessage')}
                >
                  <TextArea rows={4} />
                </Form.Item>
              ) : null;
            }}
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Card title={t('settings.requestSettings.generalSettings')} variant="outlined">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="autoProcessReceipts"
              label={t('settings.requestSettings.autoProcess')}
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="minValidationScore"
              label={t('settings.requestSettings.miniValidation')}
            >
              <InputNumber
                min={0}
                max={100}
                formatter={value => `${value}%`}
                parser={parsePercentageValue}
              />
            </Form.Item>
            <Form.Item
              name="autoProcessBonusType"
              label={t('settings.requestSettings.defaultBonus')}
            >
              <Radio.Group>
                <Radio value="fixed">{t('settings.requestSettings.fixedAmount')}</Radio>
                <Radio value="percentage">{t('settings.requestSettings.percentage')}</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              name="autoProcessBonus"
              label={t('settings.requestSettings.defaultAmount')}
            >
              <InputNumber
                min={0}
                precision={2}
              />
            </Form.Item>
            <Form.Item
              name="autoProcessBonusLoads"
              label={t('settings.requestSettings.loadsNumber')}
            >
              <InputNumber
                min={0}
                precision={0}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="loadRejectedMessage"
              label={t('settings.requestSettings.generalLoad')}
            >
              <TextArea rows={4} />
            </Form.Item>
            <Form.Item
              name="duplicateReceiptMessage"
              label={t('settings.requestSettings.duplicatedReceipt')}
            >
              <TextArea rows={4} />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title={t('settings.requestSettings.platformSettings.title')} variant="outlined">
        {platforms && platforms.length > 0 ? (
          <Collapse defaultActiveKey={['0']}>
            {platforms.map((platform, index) => (
              <Collapse.Panel 
                key={index.toString()} 
                header={platform.label}
                className="advanced-panel"
              >
                {renderPlatformSettings(platform)}
              </Collapse.Panel>
            ))}
          </Collapse>
        ) : (
          <div>No platforms configured</div>
        )}
      </Card>
    </div>
  );
};

export default RequestSettings;